import { computed, nextTick } from 'vue';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useImages from '~/composables/images/useImages';

export const NEXT_IMAGE_PEAK = 0.9;
const CAROUSEL_LENGTH_MULTIPLIER = 2;
/**
 *
 * @param {Object} object
 * @param {import('vue').Ref<number>} object.currentPage
 * @param {import('vue').Ref<number>} object.numberOfPages
 * @param {{
 *   childWidth: number,
 *   wrapWidth: number | null,
 *   itemsLength: number
 * }} object.dimensions
 * @param {import('vue').Ref<boolean>} object.noTransition
 * @param {import('vue').Ref<boolean>} object.sliding
 * @param {import('vue').Ref<number>} object.scrollerX
 * @param {import('vue').Ref<number | null>} object.scrollerWidth
 * @param {import('vue').Ref<HTMLElement | null>} object.slideshowRef
 * @param {import('vue').Ref<HTMLElement | null>} object.slideshowWrapper
 * @param {import('vue').Ref<HTMLElement | null>} object.scroller
 * @param {(eventName: 'slideshow-change' | 'load' | 'update-first-alt-dot', ...args: any[]) => void} object.emit
 */
const useSlideshowAnimation = ({
  currentPage,
  numberOfPages,
  dimensions,
  slideshowRef,
  slideshowWrapper,
  noTransition,
  sliding,
  scrollerX,
  scrollerWidth,
  scroller,
  emit,
}) => {
  const { carouselImages } = useImages();
  const { isMobile } = useDeviceType();

  const scrollerStyle = computed(() => {
    if (scrollerWidth.value != null) {
      return `
        width: ${scrollerWidth.value}px;
        transform: translateX(${scrollerX.value}px);
        transition-duration: ${noTransition.value || !sliding.value ? 0 : 300}ms;
      `;
    }
    return null;
  });

  /**
   * @returns {HTMLElement[]}
   */
  const slotsItems = () => {
    if (scroller.value) {
      return [...scroller.value.getElementsByClassName('slideshow-item')];
    }
    return [];
  };

  const reset = () => {
    scrollerWidth.value = null;
    slotsItems().forEach((item) => {
      item.style.width = null;
    });
  };
  /**
   *
   * @param {number} page - The page number (1-based)
   * @returns {number} The scroll position
   */
  const calculateScrollPosition = (page) => {
    if (numberOfPages.value === 1) {
      return 0; // Prevent offset when there's only 1 image
    }

    if (!isMobile.value) {
      // Desktop: standard calculation
      return -(dimensions.childWidth * (page - 1));
    }

    // Mobile: handle peek effect and positioning
    const pageIndex = page - 1;

    if (page === numberOfPages.value) {
      const lastImageRightAligned = -(scrollerWidth.value - dimensions.wrapWidth);
      return lastImageRightAligned;
    }

    if (page === 1) {
      return 0;
    }

    const standardPosition = -(dimensions.childWidth * pageIndex);
    const maxScrollPosition = -(scrollerWidth.value - dimensions.wrapWidth);

    return Math.max(standardPosition, maxScrollPosition);
  };

  /**
   * @param {number} [listLength]
   *
   */
  const doSizing = (listLength) => {
    if (!slideshowWrapper.value) return;

    numberOfPages.value = listLength ?? carouselImages.value.length;
    dimensions.itemsLength = numberOfPages.value + CAROUSEL_LENGTH_MULTIPLIER;
    dimensions.wrapWidth = slideshowWrapper.value.getBoundingClientRect().width;

    if (isMobile.value) {
      // If there is only 1 image, don't use NEXT_IMAGE_PEAK (no peek effect)
      const peak = numberOfPages.value === 1 ? 1 : NEXT_IMAGE_PEAK;
      dimensions.childWidth = dimensions.wrapWidth * peak;
      scrollerWidth.value = numberOfPages.value * dimensions.childWidth;
    } else {
      dimensions.childWidth = slideshowRef.value?.getBoundingClientRect().width || 0;
      scrollerWidth.value = dimensions.itemsLength * dimensions.childWidth;
    }
    slotsItems().forEach((item) => {
      item.style.width = `${dimensions.childWidth}px`;
    });

    scrollerX.value = calculateScrollPosition(currentPage.value);
  };

  /**
   * @param {number} [listLength]
   *
   */
  const init = (listLength) => {
    reset();
    nextTick(() => {
      doSizing(listLength);
      emit('load');
    });
  };

  return {
    NEXT_IMAGE_PEAK,
    scrollerStyle,
    reset,
    doSizing,
    init,
    slotsItems,
  };
};

export default useSlideshowAnimation;
